<script setup>
import avisVerifiesCodes from "@/utils/avis-verifies-codes";
import productReviewsApiDrive from "@/services/http/api-drive/magasin/produit/reviews";
import { useTrackingPage } from "@/mixins/tracking/all-pages";
import { useProductDetailsTracking } from "@/mixins/tracking/product-details";
import productInfoApiDrive from "@/services/http/api-drive/magasin/produit/productInfo";
import { LayoutTypes } from "@/services/context/layout-helper";
import { useContextStore } from "@/store/context";
import { useMagasinStore } from "@/store/magasin";
import { usePageArticle } from "../mixin";

const fetchData = async (app, shopId, productId, shopIdNational) => {
  const { getErrorStatus } = useAxiosHelper();
  try {
    const response = await app.$apiDrive
      .use(productInfoApiDrive)
      .productInfo({ shopId, productId });
    return Promise.resolve({ response, isReplacedByNational: false });
  } catch (err) {
    const errorStatus = getErrorStatus(err);
    if (errorStatus === 404) {
      try {
        const responseNational = await app.$apiDrive
          .use(productInfoApiDrive)
          .productInfo({
            shopId: shopIdNational,
            productId
          });
        return Promise.resolve({
          response: responseNational,
          isReplacedByNational: true
        });
      } catch (errNational) {
        return Promise.reject(errNational);
      }
    }
    return Promise.reject(err);
  }
};

definePageMeta({
  key: (route) => route.fullPath
});
const { getAxiosErrorFromAsyncData } = useAxiosHelper();

const { trackProductPage } = useProductDetailsTracking();
const { trackPage } = useTrackingPage();
const { id: magasinId } = useMagasinStore();
const { layout } = useContextStore();
const { path, params, query } = useRoute();
const shopId = magasinId;
const productId = params.id;
const product = ref(null);
const pageHead = ref(null);
let breadcrumb = [];
let isReplacedByNational = false;
const refreshDataClientSide = ref(false);

onMounted(async () => {
  if (product.value) {
    trackPage(
      trackingCodes.PAGETYPE.product,
      breadcrumb[0] ? breadcrumb[0].title : "",
      breadcrumb[1] ? breadcrumb[1].title : "",
      breadcrumb[2] ? breadcrumb[2].title : "",
      breadcrumb[3] ? breadcrumb[3].title : ""
    );

    trackProductPage({
      type: trackingCodes.DETAILVIEW,
      products: [product.value]
    });
  }

  // Si on a déjà un produit on a donc eu un chargement en SSR
  // On refresh la donnée si elle est trop ancienne (cache Nginx)
  if (
    product.value &&
    Date.now() - requestTime.value >
      useRuntimeConfig().public.API_DRIVE_CACHE_RELOAD_DELAY_DP
  ) {
    nextTick(() => {
      refreshDataClientSide.value = true;
    });
  }
});

const { data, error } = await useAsyncData(
  (app) =>
    fetchData(
      app,
      shopId,
      productId,
      useRuntimeConfig().public.MAGASINS_NATIONAL
    ),
  {
    watch: [refreshDataClientSide]
  }
);
if (data.value) {
  product.value = data.value.response.data;
  breadcrumb = data.value.response.meta.breadcrumb;
  isReplacedByNational = data.value.isReplacedByNational;
  if (data.value.response.meta && data.value.response.meta.head) {
    pageHead.value = data.value.response.meta.head;
  }
  // 301 SEO REDIRECTION
  if (product.value.attributes && path !== product.value.attributes.slug) {
    navigateToInternal(encodeURI(product.value.attributes.slug), {
      replace: true,
      redirectCode: 301
    });
  }
}

const { requestTime, component, created, head } = usePageArticle();

created();

const conf = useRuntimeConfig();
useHead({
  ...(pageHead.value || head(product.value)),
  meta: [
    {
      name: "apple-itunes-app",
      content: `app-id=${conf.public.IOS_CORADRIVE_APP_STORE_ID}`
    }
  ]
});

const productError = getAxiosErrorFromAsyncData(error);

if (productError) {
  if (!productError.response) {
    throw showError({
      statusCode: 504,
      statusMessage:
        "Une erreur temporaire est survenue. Veuillez recharger la page."
    });
  }

  const productErrorData = productError.response.data;

  if (productErrorData.status_code) {
    if (productErrorData.status_code === 404) {
      if (
        productErrorData.meta &&
        productErrorData.meta.breadcrumb &&
        productErrorData.meta.breadcrumb.length > 0
      ) {
        let pathToRedirect = encodeURI(
          productErrorData.meta.breadcrumb[
            productErrorData.meta.breadcrumb.length - 1
          ].slug
        );
        if (query.magasin_id) {
          pathToRedirect += "?magasin_id=" + query.magasin_id;
        }
        navigateToInternal({
          replace: true,
          path: pathToRedirect
        });
      }
    }
    throw showError({
      statusCode: productErrorData.status_code,
      statusMessage: "Une erreur est survenue."
    });
  }
}

let reviews = {};
let statistics = {};

const { data: responseReview } = await useAsyncData((app) =>
  app.$apiDrive.use(productReviewsApiDrive).reviews({
    shopId,
    productId,
    sortBy: avisVerifiesCodes.DEFAULT_SORT_METHOD,
    pageSize:
      layout === LayoutTypes.mobile
        ? avisVerifiesCodes.DEFAULT_PAGE_SIZE_MOBILE
        : avisVerifiesCodes.DEFAULT_PAGE_SIZE,
    pageIndex: avisVerifiesCodes.DEFAULT_PAGE_INDEX
  })
);
if (responseReview.value) {
  reviews = responseReview.value.data.attributes.reviews;
  statistics = responseReview.value.meta.statistics;
}
</script>

<template>
  <component
    :is="component"
    :product="product"
    :push-zones="product.attributes.push_zones"
    :breadcrumb="breadcrumb"
    :reviews="reviews"
    :statistics="statistics"
    :shop-id="shopId"
    :is-replaced-by-national="isReplacedByNational"
  />
</template>
